import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Skeleton } from "@material-ui/lab";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { notBlank } from "../util/ext";
import {
  compose,
  equals,
  filter,
  find,
  prop,
  propEq,
  sortBy,
  toLower,
  not,
  is,
} from "ramda";
import TextField from "@material-ui/core/TextField";
import { useOrganizations } from "./userServices";
import { identity } from "rxjs";
import { Box } from "@material-ui/core";
import { FixedSizeList } from "react-window";
import PropTypes from "prop-types";
import Typography from "@material-ui/core/Typography";
import { matchSorter } from "match-sorter";
import { notUUID } from "../util/mediaUtil";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref,
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 80;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemSize * itemCount;
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          style={{
            borderStyle: "solid",
            borderColor: "#F7F9FA",
            borderWidth: 2,
          }}
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
};

const filterOptions = (options, { inputValue }) =>
  matchSorter(options, inputValue, {
    keys: ["organizationId", "organizationName"],
  });

const OrganizationAutoComplete = props => {
  const {
    onChange,
    onBlur,
    value,
    label = "Organization",
    filterFunction = identity,
    valueProp = "organizationId",
    activeOnly = false,
    autoFocus = false,
    disabled = false,
    freeSolo = false,
    required = false,
    helperText,
    error,
  } = props;
  const organizations = useOrganizations();
  const [activeOrganizations, setActiveOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const filterFunctionCallback = useCallback(filterFunction, [filterFunction]);

  useEffect(() => {
    if (organizations.isSuccess) {
      const orgList = sortBy(
        compose(toLower, prop("organizationName")),
        filter(
          org => !activeOnly || org.active,
          filter(filterFunctionCallback, organizations.data),
        ),
      );

      setActiveOrganizations(orgList);

      const selected = find(propEq(valueProp, value), orgList);
      // if value is not UUID, assume it is an org name
      if (notBlank(value) && notUUID(value)) {
        setSelectedOrganization({ organizationName: value, active: true });
      } else {
        setSelectedOrganization(selected);
      }
    }
  }, [
    activeOnly,
    filterFunctionCallback,
    organizations.data,
    organizations.isSuccess,
    valueProp,
    value,
  ]);

  const handleOrganizationSelect = useCallback(
    (_event, organization) => {
      // if organization is string and autocomplete is freeSolo
      // assume organization is organizationName and add to orgList
      const org = is(String, organization)
        ? { organizationName: organization, active: true }
        : organization;

      if (is(String, organization)) {
        setActiveOrganizations(prev => [...prev, org]);
      }

      setSelectedOrganization(org);
      onChange(org ? org[valueProp] : null);
    },
    [onChange, valueProp],
  );

  return (
    <Fragment>
      {organizations.isLoading ? (
        <Skeleton variant="text" />
      ) : (
        <Autocomplete
          freeSolo={freeSolo}
          disabled={disabled}
          disableListWrap
          filterOptions={filterOptions}
          options={activeOrganizations}
          value={selectedOrganization || null}
          ListboxComponent={ListboxComponent}
          getOptionLabel={org => notBlank(org) && `${org.organizationName}`}
          getOptionSelected={(opt, val) =>
            notBlank(val) && equals(opt.organizationId, val.organizationId)
          }
          renderOption={(option, { selected }) => (
            <Box width="100%">
              <Box fontSize="1.4rem">
                <Typography
                  noWrap
                  color={not(option.active) ? "error" : "inherit"}
                >
                  {option.organizationName}
                </Typography>
              </Box>
              {option.organizationId && (
                <Box fontSize="1.2rem">
                  <Typography color={not(option.active) ? "error" : "inherit"}>
                    {`(${option.organizationId})`}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          onChange={handleOrganizationSelect}
          onBlur={onBlur}
          renderInput={params => {
            params.inputProps["aria-autocomplete"] = "none";
            params.inputProps["id"] = null;
            return (
              <TextField
                {...params}
                required={required}
                autoFocus={autoFocus}
                type="text"
                name={"program-organization-" + Math.random()}
                variant="standard"
                label={label}
                error={error}
                helperText={helperText}
                fullWidth
              />
            );
          }}
        />
      )}
    </Fragment>
  );
};

OrganizationAutoComplete.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  filterFunction: PropTypes.func,
  activeOnly: PropTypes.bool,
  autoFocus: PropTypes.bool,
  helperText: PropTypes.string,
  error: PropTypes.bool,
};

export default OrganizationAutoComplete;
